import { useState, useEffect, useCallback } from 'react';
import { fetchBNBPrice } from '../utils/priceAPI';

interface BNBPriceState {
  price: number;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
}

/**
 * Hook for managing BNB price with automatic updates
 */
export function useBNBPrice() {
  const [state, setState] = useState<BNBPriceState>({
    price: 600, // Default fallback price
    loading: true,
    error: null,
    lastUpdated: 0
  });

  const updatePrice = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      const price = await fetchBNBPrice();
      setState({
        price,
        loading: false,
        error: null,
        lastUpdated: Date.now()
      });
    } catch (error) {
      console.error('Failed to update BNB price:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch price'
      }));
    }
  }, []);

  // Initial price fetch
  useEffect(() => {
    updatePrice();
  }, [updatePrice]);

  // Set up automatic price updates every 60 seconds
  useEffect(() => {
    const interval = setInterval(updatePrice, 60 * 1000);
    return () => clearInterval(interval);
  }, [updatePrice]);

  return {
    price: state.price,
    loading: state.loading,
    error: state.error,
    lastUpdated: state.lastUpdated,
    refetch: updatePrice
  };
}